import {
  type ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldProps } from '@rjsf/utils';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import { InfoAlert } from '../InfoAlertField/InfoAlertField';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { useCatalogUserLookup } from '../../../hooks/useCatalogUserLookup';

type RowData = Record<string, string | undefined>;
type FieldStatus = 'default' | 'success' | 'error';

export const InputGroupArrayCheckboxCard = ({
  onChange,
  formData,
  formContext,
  schema,
  idSchema,
  disabled,
  readonly,
  uiSchema,
}: FieldProps<RowData[]>) => {
  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  // Fetch technical contacts from the form context
  const techContacts = formContext.formData?.technicalContacts || [];
  const techContactsString = JSON.stringify(techContacts);

  // State for checkbox: whether to use technical contacts
  const [isSameAsTechContacts, setIsSameAsTechContacts] = useState(true);

  const [localValues, setLocalValues] = useState<RowData[]>(() => {
    if (Array.isArray(formData) && formData.length > 0) return formData;
    if (techContacts.length > 0) return techContacts;
    return [
      { userId: '', email: '' },
      { userId: '', email: '' },
    ];
  });

  const [minimumValuesError, setMinimumValuesError] = useState<string | null>(
    null,
  );

  const timersRef = useRef<
    Record<string, ReturnType<typeof setTimeout> | undefined>
  >({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [fieldStatus, setFieldStatus] = useState<Record<string, FieldStatus>>(
    {},
  );
  const [fieldHelpers, setFieldHelpers] = useState<Record<string, string>>({});
  const debounceMs = 500;

  const { findUserByUserId, findUserByEmail, isEmail } = useCatalogUserLookup();

  const values: RowData[] = localValues;

  const itemsSchema = useMemo(() => (schema as any)?.items ?? {}, [schema]);
  const propsSchema = useMemo(
    () =>
      (itemsSchema?.properties ?? {}) as Record<
        string,
        {
          title?: string;
          format?: string;
          description?: string;
          const?: string;
          default?: string;
        }
      >,
    [itemsSchema],
  );
  const uiItems = useMemo(() => (uiSchema as any)?.items ?? {}, [uiSchema]);

  // Extract const values for hidden fields (e.g. type: ALICE_ADMIN) so they are
  // automatically injected into every row emitted via onChange.
  const constValues: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(propsSchema)
          .filter(k => propsSchema[k]?.const !== undefined)
          .map(k => [
            k,
            String(propsSchema[k]?.const ?? propsSchema[k]?.default ?? ''),
          ]),
      ),
    [propsSchema],
  );

  const selectedKeys = ['userId', 'email'];
  const userIdKey = 'userId';
  const emailKey = 'email';

  const labels: Record<string, string> = Object.fromEntries(
    selectedKeys.map(k => [k, propsSchema[k]?.title ?? k]),
  );
  const types: Record<string, string> = Object.fromEntries(
    selectedKeys.map(k => [
      k,
      propsSchema[k]?.format === 'email' ? 'email' : 'text',
    ]),
  );

  const minItems = typeof schema.minItems === 'number' ? schema.minItems : 2;
  const maxItems = typeof schema.maxItems === 'number' ? schema.maxItems : 5;

  const infoText = (uiSchema as any)?.['ui:options']?.infoText as
    | string
    | undefined;

  const addButtonLabel =
    ((uiSchema as any)?.['ui:options']?.addButtonLabel as string | undefined) ??
    'Add Person';

  const canAdd = !disabled && !readonly && values.length < maxItems;

  // Cleanup for debounce timers
  useEffect(() => {
    const timersSnapshot = timersRef.current;
    return () => {
      Object.values(timersSnapshot).forEach(t => t && clearTimeout(t));
    };
  }, []);

  // Effect for checkbox and reference synchronization
  useEffect(() => {
    if (isSameAsTechContacts) {
      const parsedTechContacts = JSON.parse(techContactsString);
      // Override the `type` (and any other const fields) with this field's own
      // const values — technical contacts have type TECHNICAL_CONTACT, but this
      // field may need ALICE_ADMIN or N8N_ADMIN.
      const remappedContacts = parsedTechContacts.map((r: RowData) => ({
        ...r,
        ...constValues,
      }));
      setLocalValues(remappedContacts);

      // Only call onChange if parent form data is not up to date
      if (JSON.stringify(formData) !== JSON.stringify(remappedContacts)) {
        // setTimeout solves the RJSF race condition on initial mount
        const timer = setTimeout(() => {
          onChange(remappedContacts);
        }, 100);
        return () => clearTimeout(timer);
      }
      return undefined;
    }
    return undefined;
  }, [
    constValues,
    isSameAsTechContacts,
    techContactsString,
    formData,
    onChange,
  ]);
  // ---------------------------------------------------------------------------

  // Clear fields when checkbox is unchecked
  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsSameAsTechContacts(checked);

    if (!checked) {
      const emptyRows = [
        { ...constValues, userId: '', email: '' },
        { ...constValues, userId: '', email: '' },
      ];
      setLocalValues(emptyRows);
      onChange(emptyRows);

      setFieldStatus({});
      setFieldHelpers({});
      setMinimumValuesError(null);
    }
  };

  // Effect for manual input validation
  useEffect(() => {
    if (isSameAsTechContacts) return undefined;

    const firstTwoRowsValid = values.slice(0, 2).every(row => {
      return (
        typeof row?.[userIdKey] === 'string' &&
        row[userIdKey].trim().length > 0 &&
        typeof row?.[emailKey] === 'string' &&
        row[emailKey].trim().length > 0
      );
    });

    if (firstTwoRowsValid) {
      setMinimumValuesError(null);
    }

    // Only call onChange if data has actually changed.
    // Always inject hidden const-value fields (e.g. type) before emitting.
    const withConst = values.map(r => ({ ...constValues, ...r }));
    if (JSON.stringify(formData) !== JSON.stringify(withConst)) {
      const timer = setTimeout(() => {
        onChange(withConst);
      }, 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [constValues, values, isSameAsTechContacts, formData, onChange]);

  function setCellLoading(row: number, key: string, v: boolean) {
    setLoadingMap(prev => ({ ...prev, [`${row}:${key}`]: v }));
  }

  function setFieldState(
    row: number,
    key: string,
    status: FieldStatus,
    text: string,
  ) {
    const fk = `${row}:${key}`;
    setFieldStatus(prev => ({ ...prev, [fk]: status }));
    setFieldHelpers(prev => ({ ...prev, [fk]: text }));
  }

  // Catalog Lookups
  async function handleUserIdLookup(rowIndex: number, value: string) {
    if (value.trim().length === 0) return;
    setCellLoading(rowIndex, userIdKey, true);
    setFieldState(
      rowIndex,
      userIdKey,
      'default',
      'Searching user in catalog...',
    );

    const user = await findUserByUserId(value).finally(() =>
      setCellLoading(rowIndex, userIdKey, false),
    );

    if (!user) {
      const defaultText = propsSchema[userIdKey]?.description ?? '';
      setFieldState(rowIndex, userIdKey, 'default', defaultText);
      return;
    }

    const email = (user as any)?.spec?.profile?.email ?? '';
    if (email) {
      setLocalValues(prev => {
        const next = [...prev];
        const row = { ...(next[rowIndex] ?? {}) };
        if (row[emailKey] !== email) row[emailKey] = email;
        next[rowIndex] = row;
        return next;
      });
      setFieldState(rowIndex, userIdKey, 'success', 'Success! User found.');
      setFieldState(rowIndex, emailKey, 'success', 'Success! Email obtained.');
    } else {
      setFieldState(
        rowIndex,
        userIdKey,
        'default',
        'Error. User found but no email available.',
      );
    }
  }

  async function handleEmailLookup(rowIndex: number, value: string) {
    if (!isEmail(value)) return;
    setCellLoading(rowIndex, emailKey, true);
    setFieldState(
      rowIndex,
      emailKey,
      'default',
      'Searching user in catalog...',
    );

    const user = await findUserByEmail(value).finally(() =>
      setCellLoading(rowIndex, emailKey, false),
    );

    if (!user) {
      const defaultText = propsSchema[emailKey]?.description ?? '';
      setFieldState(rowIndex, emailKey, 'default', defaultText);
      return;
    }

    const id = user.metadata?.name ?? '';
    if (id) {
      setLocalValues(prev => {
        const next = [...prev];
        const row = { ...(next[rowIndex] ?? {}) };
        if (row[userIdKey] !== id) row[userIdKey] = id;
        next[rowIndex] = row;
        return next;
      });
      setFieldState(rowIndex, emailKey, 'success', 'Success! Email found.');
      setFieldState(
        rowIndex,
        userIdKey,
        'success',
        'Success! User ID obtained.',
      );
    }
  }

  async function handleLinkedLookup(
    rowIndex: number,
    inputKey: string,
    value: string,
  ) {
    if (!value.trim()) return;
    if (inputKey === userIdKey) handleUserIdLookup(rowIndex, value);
    else if (inputKey === emailKey) handleEmailLookup(rowIndex, value);
  }

  function matchesPattern(value: string, pattern?: string): boolean {
    if (!pattern) return true;
    try {
      return new RegExp(pattern).test(value);
    } catch (e) {
      return false;
    }
  }

  const checkFocusedButEmptyFieldAndMandatory = (
    rowIndex: number,
    key: string,
  ) => {
    const next = [...values];
    const row = { ...(next[rowIndex] ?? {}) };
    const value = typeof row[key] === 'string' ? (row[key] ?? '') : '';
    const pattern = (propsSchema[key] as any)?.pattern;

    const errorMessage =
      key === emailKey
        ? ((propsSchema[key] as any)?.errorMessage ?? 'Invalid email format.')
        : ((propsSchema[key] as any)?.errorMessage ??
          'Invalid User ID format.');

    if (value.trim().length === 0) {
      setFieldState(rowIndex, key, 'error', 'Error. This is a required field.');
      if (rowIndex < 2) {
        setMinimumValuesError(
          `Error. Provide user ID and email of at least ${minItems} persons.`,
        );
      }
      return true;
    }

    if (pattern && !matchesPattern(value, pattern)) {
      setFieldState(rowIndex, key, 'error', errorMessage);
      return true;
    }

    return false;
  };

  const handleChange =
    (rowIndex: number, key: string) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const next = [...values];
      const row = { ...(next[rowIndex] ?? {}) };
      row[key] = e.target.value;
      next[rowIndex] = row;

      if (!checkFocusedButEmptyFieldAndMandatory(rowIndex, key)) {
        const defaultText = propsSchema[key]?.description ?? '';
        setFieldState(rowIndex, key, 'default', defaultText);
      }

      setLocalValues(next);

      if (key === userIdKey || key === emailKey) {
        const timerKey = `${rowIndex}:${key}`;
        if (timersRef.current[timerKey])
          clearTimeout(timersRef.current[timerKey]);
        timersRef.current[timerKey] = setTimeout(() => {
          handleLinkedLookup(rowIndex, key, e.target.value);
        }, debounceMs);
      }
    };

  function handleAdd() {
    setLocalValues([...values, { userId: '', email: '' }]);
  }

  function handleRemove(rowIndex: number) {
    const next = [...values];
    next.splice(rowIndex, 1);
    setLocalValues(next);
  }

  return (
    <Box sx={{ backgroundColor, padding: 2, borderRadius: 1 }}>
      {schema.title && (
        <Box component="h2" sx={{ marginBottom: 1, marginTop: 0 }}>
          {schema.title}
        </Box>
      )}

      {schema.description && (
        <Box sx={{ marginBottom: 1 }}>{schema.description}</Box>
      )}

      {(schema.title || schema.description) && (
        <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
      )}

      {/* Checkbox */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isSameAsTechContacts}
              onChange={handleCheckboxChange}
              disabled={disabled || readonly}
            />
          }
          label="Same as Technical contacts"
        />
      </Box>

      <InfoAlert text={infoText} sx={{ mb: 3 }} />

      {/* Input fields (only visible when checkbox is off) */}
      {!isSameAsTechContacts && (
        <>
          {minimumValuesError && minimumValuesError.trim().length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {minimumValuesError}
            </Alert>
          )}

          <Stack spacing={3}>
            {values.map((row, i) => {
              const canRemove =
                !disabled && !readonly && values.length > minItems;
              return (
                <Fragment key={`${idSchema.$id}_row_${i}`}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Stack direction="column" spacing={2} sx={{ flex: 1 }}>
                      {selectedKeys.map(key => {
                        const placeholder = uiItems?.[key]?.['ui:placeholder'];
                        const fk = `${i}:${key}`;
                        const status = fieldStatus[fk] ?? 'default';
                        return (
                          <TextField
                            key={`${idSchema.$id}_${i}_${key}`}
                            id={`${idSchema.$id}_${i}_${key}`}
                            label={labels[key]}
                            placeholder={placeholder}
                            required
                            value={row?.[key] ?? ''}
                            onChange={handleChange(i, key)}
                            onBlur={() =>
                              checkFocusedButEmptyFieldAndMandatory(i, key)
                            }
                            disabled={disabled || readonly}
                            type={types[key]}
                            variant="outlined"
                            fullWidth
                            error={status === 'error'}
                            color={status === 'success' ? 'success' : undefined}
                            focused={status === 'success'}
                            helperText={
                              fieldHelpers[fk] ??
                              propsSchema[key]?.description ??
                              ''
                            }
                            InputProps={{
                              endAdornment: loadingMap[fk] ? (
                                <InputAdornment position="end">
                                  <CircularProgress size={18} />
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                        );
                      })}
                    </Stack>

                    <IconButton
                      aria-label="Remove"
                      onClick={() => handleRemove(i)}
                      disabled={!canRemove}
                      size="medium"
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Stack>
                  {i < values.length - 1 && <Divider sx={{ my: 2 }} />}
                </Fragment>
              );
            })}
          </Stack>

          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleAdd}
              disabled={!canAdd || disabled || readonly}
              size="small"
              startIcon={<AddCircleOutlineIcon />}
            >
              {addButtonLabel}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
